import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import { Plus, UserCircle, Trash, ShieldCheck, Eye, EyeSlash } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Validation helpers
const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Field-level errors
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
  });
  
  // Track which fields have been touched (for blur validation)
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
  });
  
  // Ref for autofocus
  const nameInputRef = useRef(null);

  useEffect(() => { 
    fetchAdmins(); 
  }, []);
  
  // Autofocus name input when dialog opens
  useEffect(() => {
    if (isDialogOpen && nameInputRef.current) {
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isDialogOpen]);

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${API}/admin/admins`, { credentials: "include" });
      if (response.ok) setAdmins((await response.json()).admins);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Validate a single field
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 2) return "Name must be at least 2 characters";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!validateEmail(value.trim())) return "Please enter a valid email";
        return "";
      case "password":
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        return "";
      default:
        return "";
    }
  };
  
  // Handle field change with live validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing (if field was touched)
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };
  
  // Handle field blur - validate on blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };
  
  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.name.trim().length >= 2 &&
      validateEmail(formData.email.trim()) &&
      formData.password.length >= 8
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {
      name: validateField("name", formData.name),
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
    };
    
    setErrors(newErrors);
    setTouched({ name: true, email: true, password: true });
    
    // Check if any errors
    if (Object.values(newErrors).some(err => err)) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Use XMLHttpRequest to avoid fetch interceptors that may consume the response
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/admin/admins`);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch {
            resolve({ status: xhr.status, data: { detail: xhr.responseText } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
        }));
      });
      
      if (result.status === 200) {
        toast.success("Admin created successfully");
        handleCloseDialog();
        fetchAdmins();
      } else {
        const err = result.data;
        console.log("[AdminAdmins] Error response:", err, "Status:", result.status);
        
        // Handle specific error codes
        if (err.detail?.code === "EMAIL_EXISTS") {
          setErrors(prev => ({ ...prev, email: err.detail.message || "Email is already in use" }));
          setTouched(prev => ({ ...prev, email: true })); // Ensure error is displayed
        } else if (err.detail?.code === "VALIDATION_ERROR") {
          const field = err.detail.field;
          if (field && errors[field] !== undefined) {
            setErrors(prev => ({ ...prev, [field]: err.detail.message }));
            setTouched(prev => ({ ...prev, [field]: true })); // Ensure error is displayed
          } else {
            toast.error(err.detail.message || "Validation error");
          }
        } else if (result.status === 401 || result.status === 403) {
          toast.error("Not authorized to create admins");
        } else {
          // Generic server error
          const message = typeof err.detail === 'string' ? err.detail : "Could not create admin";
          toast.error(message);
        }
      }
    } catch (error) {
      console.error("[AdminAdmins] Catch block error:", error);
      toast.error("Could not create admin. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ name: "", email: "", password: "" });
    setErrors({ name: "", email: "", password: "" });
    setTouched({ name: false, email: false, password: false });
    setShowPassword(false);
  };

  const deleteAdmin = async (userId) => {
    if (!window.confirm("Delete this admin?")) return;
    try {
      const response = await fetch(`${API}/admin/admins/${userId}`, { 
        method: "DELETE", 
        credentials: "include" 
      });
      if (response.ok) {
        toast.success("Admin deleted");
        fetchAdmins();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-admins">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white uppercase">
          Platform Administrators
        </h1>
        <Button 
          onClick={() => setIsDialogOpen(true)}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          data-testid="add-admin-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Admin
        </Button>
        <AdminRecordModal
          open={isDialogOpen}
          onOpenChange={(open) => open ? setIsDialogOpen(true) : handleCloseDialog()}
          title="Create Platform Admin"
          description="Admins can access all tenants and support tools."
          size="md"
          data-testid="create-admin-modal"
          footer={
            <AdminModalFooter onCancel={handleCloseDialog} cancelLabel={submitting ? "Cancel" : "Cancel"}>
              <Button
                type="submit"
                form="create-admin-form"
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                disabled={!isFormValid() || submitting}
                data-testid="create-admin-submit"
              >
                {submitting ? "Creating..." : "Create Admin"}
              </Button>
            </AdminModalFooter>
          }
        >
          <form id="create-admin-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Name Field */}
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium text-zinc-300">Name *</Label>
              <Input
                ref={nameInputRef}
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="John Smith"
                className={`h-11 bg-zinc-800 border-zinc-600 text-white ${errors.name && touched.name ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="admin-name-input"
                aria-invalid={errors.name && touched.name ? "true" : "false"}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && touched.name && (
                <p id="name-error" className="text-sm text-red-500" data-testid="name-error">
                  {errors.name}
                </p>
              )}
            </div>
            
            {/* Email Field */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-zinc-300">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="admin@example.com"
                className={`h-11 bg-zinc-800 border-zinc-600 text-white ${errors.email && touched.email ? 'border-red-500 focus:border-red-500' : ''}`}
                data-testid="admin-email-input"
                aria-invalid={errors.email && touched.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && touched.email && (
                <p id="email-error" className="text-sm text-red-500" data-testid="email-error">
                  {errors.email}
                </p>
              )}
            </div>
            
            {/* Password Field with show/hide toggle */}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-zinc-300">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Minimum 8 characters"
                  className={`h-11 bg-zinc-800 border-zinc-600 text-white pr-10 ${errors.password && touched.password ? 'border-red-500 focus:border-red-500' : ''}`}
                  data-testid="admin-password-input"
                  aria-invalid={errors.password && touched.password ? "true" : "false"}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && touched.password && (
                <p id="password-error" className="text-sm text-red-500" data-testid="password-error">
                  {errors.password}
                </p>
              )}
            </div>
          </form>
        </AdminRecordModal>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-800 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : admins.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <ShieldCheck size={48} className="mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">No platform administrators found.</p>
            <p className="text-sm text-zinc-500 mt-2">Click &quot;Add Admin&quot; to create the first one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {admins.map((admin) => (
            <Card key={admin.user_id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FF6600]/20 flex items-center justify-center rounded-full">
                    <UserCircle size={28} className="text-[#FF6600]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{admin.name}</p>
                    <p className="text-sm text-zinc-500 truncate">{admin.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteAdmin(admin.user_id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 shrink-0"
                    data-testid={`delete-admin-${admin.user_id}`}
                  >
                    <Trash size={18} />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <ShieldCheck size={16} className="text-emerald-400" />
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
                    Platform Admin
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
